import React, { useState, useRef } from "react";
import axiosInstance from "../../services/FetchNodeServices";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const TxtStockUpdate = () => {
  const [jsonData, setJsonData] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file || file.type !== "text/plain") {
      toast.error("Please upload a valid .txt file");
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const parsedJson = convertTxtToJson(text);
      setJsonData(parsedJson);
      toast.success(`✅ File parsed! ${parsedJson.length} products found.`, { autoClose: 2000 });
    };
    reader.readAsText(file);
  };

  const convertTxtToJson = (text) => {
    const lines = text.trim().split("\n");
    const headers = lines[0].split("##").map((h) => h.trim());
    return lines.slice(1).map((line) => {
      const values = line.split("##").map((v) => v.trim());
      const obj = {};
      headers.forEach((key, index) => { obj[key] = values[index] ?? ""; });
      return obj;
    });
  };

  const CHUNK_SIZE = 5000;

const uploadInChunks = async (data, zeroOutMissing) => {
    const totalChunks = Math.ceil(data.length / CHUNK_SIZE);
    let successChunks = 0;

    // ✅ Collect ALL ISBN13s upfront for zeroing out
    const allISBN13s = data.map(p => String(p.ISBN13));

    for (let i = 0; i < data.length; i += CHUNK_SIZE) {
      const chunk = data.slice(i, i + CHUNK_SIZE);
      const currentChunk = i / CHUNK_SIZE + 1;
      const percent = Math.round((currentChunk / totalChunks) * 100);
      const isLastChunk = currentChunk === totalChunks;

      Swal.update({
        html: `
          <p style="font-size: 15px; color: #555; margin-bottom:10px;">
            Updating chunk <strong>${currentChunk}</strong> of <strong>${totalChunks}</strong>
          </p>
          <div class="progress" style="height: 20px; border-radius:10px;">
            <div class="progress-bar progress-bar-striped progress-bar-animated bg-info"
              role="progressbar" style="width: ${percent}%; font-size:12px;">
              ${percent}%
            </div>
          </div>
          <p style="font-size: 12px; color: #888; margin-top:10px;">
            📦 Processed: <strong>${Math.min(i + CHUNK_SIZE, data.length)}</strong> / ${data.length}
          </p>
          <p style="font-size: 12px; color: #888;">
            ✅ Completed chunks: <strong>${successChunks}</strong> / ${totalChunks}
          </p>
          <p style="font-size: 12px; color: ${isLastChunk && zeroOutMissing ? 'orange' : '#888'};">
            ${isLastChunk && zeroOutMissing
              ? "⚠️ Last chunk — setting missing products to stock 0"
              : "🔄 Updating stock for this batch..."}
          </p>
        `,
      });

      try {
        await axiosInstance.post(
          "/api/v1/product/manage-product-stock",
          {
            products: chunk,
            isLastChunk: isLastChunk,
            zeroOutMissing: zeroOutMissing,
            // ✅ Send ALL ISBN13s only on last chunk to avoid large payload
            allISBN13s: isLastChunk && zeroOutMissing ? allISBN13s : [],
          },
          { headers: { "Content-Type": "application/json; charset=utf-8" } }
        );
        successChunks++;
      } catch (error) {
        Swal.close();
        await Swal.fire({
          icon: "error",
          title: "❌ Upload Failed!",
          html: `
            <div style="text-align:left; background:#f8d7da; padding:15px; border-radius:8px;">
              <p style="font-size:14px; color:#721c24; font-weight:bold; margin-bottom:10px;">
                Failed at chunk ${currentChunk} of ${totalChunks}
              </p>
              <ul style="font-size:13px; color:#555; padding-left:20px;">
                <li>✅ <strong>${successChunks} chunks</strong> completed</li>
                <li>❌ <strong>Chunk ${currentChunk}</strong> failed</li>
                <li>⏳ <strong>${totalChunks - currentChunk}</strong> chunks remaining</li>
              </ul>
              <p style="font-size:13px; color:red; margin-top:10px; font-weight:bold;">
                ⚠️ Please re-upload the FULL file to complete the update.
              </p>
            </div>
          `,
          confirmButtonText: "OK",
          confirmButtonColor: "#d33",
        });
        throw error;
      }
    }
  };
  const handleSubmit = async () => {
    if (!jsonData.length) {
      toast.error("No data to upload. Please upload a .txt file.");
      return;
    }
    if (!jsonData[0]?.ISBN13) {
      toast.error("ISBN13 field is required.");
      return;
    }

    // ✅ Ask user to choose mode
    const confirm = await Swal.fire({
      title: "⚠️ Confirm Stock Update",
      html: `
        <div style="text-align:left; padding:10px;">
          <p style="font-size:14px; color:#333; font-weight:bold; margin-bottom:15px;">
            Choose update mode:
          </p>

          <div style="background:#d4edda; border:1px solid #28a745; border-radius:8px; padding:12px; margin-bottom:10px;">
            <label style="font-size:13px; cursor:pointer; display:flex; align-items:flex-start; gap:8px;">
              <input type="radio" name="mode" value="partial" checked style="margin-top:3px;">
              <div>
                <strong style="color:#155724;">✅ Partial Update (Recommended)</strong><br/>
                <span style="color:#555;">Only update products IN this file. Products NOT in file remain unchanged.</span>
              </div>
            </label>
          </div>

          <div style="background:#f8d7da; border:1px solid #dc3545; border-radius:8px; padding:12px;">
            <label style="font-size:13px; cursor:pointer; display:flex; align-items:flex-start; gap:8px;">
              <input type="radio" name="mode" value="full" style="margin-top:3px;">
              <div>
                <strong style="color:#721c24;">🔴 Full Replace (Dangerous)</strong><br/>
                <span style="color:#555;">Update file products + set ALL others to stock 0. Use only if file has ALL products.</span>
              </div>
            </label>
          </div>

          <hr style="margin:12px 0;">
          <p style="font-size:12px; color:#666; margin:0;">
            📊 Products in file: <strong>${jsonData.length}</strong>
          </p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Proceed",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      preConfirm: () => {
        return document.querySelector('input[name="mode"]:checked').value;
      }
    });

    if (!confirm.isConfirmed) return;

    const zeroOutMissing = confirm.value === "full";

    Swal.fire({
      title: "<strong>📦 Updating Stock</strong>",
      html: `
        <p style="font-size: 15px; color: #555; margin-bottom:10px;">
          Starting stock update for <strong>${jsonData.length}</strong> products...
        </p>
        <div class="progress" style="height: 20px; border-radius:10px;">
          <div class="progress-bar progress-bar-striped progress-bar-animated bg-info"
            style="width: 5%;">5%</div>
        </div>
        <p style="font-size: 12px; color: #888; margin-top:10px;">
          ${zeroOutMissing
            ? "⚠️ Mode: FULL REPLACE — Products not in file will be set to stock 0"
            : "✅ Mode: PARTIAL UPDATE — Only updating products in file"
          }
        </p>
      `,
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      setDisabled(true);
      await uploadInChunks(jsonData, zeroOutMissing);

      Swal.close();
      Swal.fire({
        icon: "success",
        title: "✅ Stock Updated!",
        html: `
          <div style="text-align:left; background:#f8f9fa; padding:15px; border-radius:8px;">
            <p style="font-size:15px; color:#444; margin-bottom:10px;">
              Stock updated successfully!
            </p>
            <hr/>
            <ul style="font-size:13px; color:#555; padding-left:20px; margin-bottom:0;">
              <li>✅ <strong>${jsonData.length}</strong> products updated with their quantities</li>
              ${zeroOutMissing
                ? '<li>🔴 Products <strong>NOT in file</strong> → stock set to <strong>0</strong></li>'
                : '<li>✅ Products <strong>NOT in file</strong> → stock <strong>UNCHANGED</strong></li>'
              }
              <li>📝 Only <strong>stock field</strong> was changed</li>
            </ul>
          </div>
        `,
        confirmButtonText: "Done ✅",
        confirmButtonColor: "#3085d6",
      });

      setDisabled(false);
      setJsonData([]);
      if (fileInputRef.current) fileInputRef.current.value = "";

    } catch (error) {
      setDisabled(false);
      Swal.close();
      toast.error("❌ Stock update failed. Please try again.");
    }
  };

  return (
    <div className="container mt-5">
      <div className="card shadow">
        <div className="card-body">
          <div className="p-4 mb-2 text-start">
            <h4 className="card-title mb-3">Update Stock (Txt)</h4>
            <p>File Name: <span style={{ color: "red", fontSize: "18px" }}>Originalproduct.txt</span></p>

            {/* ℹ️ Info Note */}
            <div style={{
              background: "#e8f4fd",
              border: "1px solid #3498db",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "12px"
            }}>
              <p style={{ fontWeight: "bold", color: "#2980b9", marginBottom: "8px" }}>
                ℹ️ Two modes available:
              </p>
              <ul style={{ fontSize: "13px", color: "#555", paddingLeft: "20px", marginBottom: "0" }}>
                <li>✅ <strong>Partial Update</strong> — Only updates products in file. Safe for partial stock files.</li>
                <li>🔴 <strong>Full Replace</strong> — Updates file products + sets ALL others to stock 0. Use only if file has ALL products.</li>
                <li>🔍 <strong>Matches by:</strong> ISBN13</li>
                <li>📦 <strong>Uploads in chunks</strong> of 5000 products per request</li>
              </ul>
            </div>

            {/* ✅ Required Fields */}
            <div style={{
              background: "#d4edda",
              border: "1px solid #28a745",
              borderRadius: "8px",
              padding: "12px",
            }}>
              <p style={{ fontWeight: "bold", color: "#155724", marginBottom: "8px" }}>
                ✅ Required columns in TXT file:
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {["ISBN13", "PRODUCTS_QUANTITY"].map(field => (
                  <span key={field} style={{
                    background: "#28a745",
                    color: "white",
                    padding: "3px 10px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "bold"
                  }}>{field}</span>
                ))}
              </div>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".txt"
            onChange={handleFileChange}
            className="form-control mb-3"
          />

          {jsonData.length > 0 && (
            <div className="alert alert-info">
              📊 <strong>{jsonData.length}</strong> products ready to update
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={disabled || jsonData.length === 0}
            className="btn btn-primary"
          >
            {disabled ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Updating...
              </>
            ) : "Update Stock"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TxtStockUpdate;
